import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { WorkspaceHeader } from "./WorkspaceHeader";
import { OperatorSurface } from "../ui/OperatorSurface";
import { Button } from "../ui/Button";
import { cn } from "../../lib/utils";

export type NonWorkspacePageWidth = "narrow" | "standard" | "wide" | "dense";

export type NonWorkspacePageHeader = {
  eyebrow: string;
  title: string;
  description?: ReactNode;
  breadcrumbs?: Array<{ label: string; href?: string | null }>;
  badges?: ReactNode;
  metadata?: ReactNode;
};

export type WorkspaceRowAction = {
  href: string;
  label: "Open Result" | "Review Result";
  state?: unknown;
};

export type NonWorkspacePrimaryAction = {
  label: string;
  onClick: () => void;
  disabled?: boolean;
};

const widthClassName: Record<NonWorkspacePageWidth, string> = {
  narrow: "max-w-4xl",
  standard: "max-w-5xl",
  wide: "max-w-6xl",
  dense: "max-w-7xl",
};

/**
 * The calm, rail-adjacent page frame for queues, histories, libraries, and
 * account configuration. Active work objects remain in Workspace instead.
 */
export function NonWorkspacePage({
  width = "standard",
  header,
  primaryAction,
  secondaryActions,
  toolbar,
  notice,
  children,
  className,
}: {
  width?: NonWorkspacePageWidth;
  header: NonWorkspacePageHeader;
  /** A page gets one primary action at most. Omit it for observation/configuration pages. */
  primaryAction?: NonWorkspacePrimaryAction;
  /** Lower-priority actions belong beside, rather than competing with, the primary action. */
  secondaryActions?: ReactNode;
  toolbar?: ReactNode;
  notice?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn("mx-auto w-full space-y-6 py-4 md:py-5", widthClassName[width], className)}
      data-page-family="non-workspace"
      data-page-width={width}
    >
      <WorkspaceHeader
        {...header}
        headingActions={primaryAction ? (
          <div data-page-action="primary">
            <Button type="button" onClick={primaryAction.onClick} disabled={primaryAction.disabled}>
              {primaryAction.label}
            </Button>
          </div>
        ) : undefined}
        actions={secondaryActions ? <div data-page-action="secondary">{secondaryActions}</div> : undefined}
      />
      {toolbar ? <div data-page-slot="toolbar">{toolbar}</div> : null}
      {notice ? <div data-page-slot="notice">{notice}</div> : null}
      {children}
    </div>
  );
}

export function PageLoadingState({ children = "Loading this page…" }: { children?: ReactNode }) {
  return (
    <div role="status" aria-live="polite">
      <OperatorSurface className="flex min-h-40 items-center justify-center text-center text-sm text-slate-600">
        {children}
      </OperatorSurface>
    </div>
  );
}

export function PageEmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description: ReactNode;
  action?: ReactNode;
}) {
  return (
    <OperatorSurface className="p-8 text-center">
      <h2 className="text-base font-semibold text-slate-900">{title}</h2>
      <div className="mx-auto mt-2 max-w-lg text-sm leading-6 text-slate-600">{description}</div>
      {action ? <div className="mt-4 flex justify-center">{action}</div> : null}
    </OperatorSurface>
  );
}

export function PageErrorState({
  title = "This page could not be loaded",
  description,
  action,
  details,
}: {
  title?: string;
  description: ReactNode;
  action?: ReactNode;
  details?: ReactNode;
}) {
  return (
    <div role="alert">
      <OperatorSurface className="border-red-200 bg-red-50">
        <h2 className="text-base font-semibold text-red-900">{title}</h2>
        <div className="mt-1 text-sm leading-6 text-red-800">{description}</div>
        {action ? <div className="mt-4">{action}</div> : null}
        {details ? <details className="mt-4 text-sm text-red-800"><summary className="cursor-pointer font-medium">Technical details</summary><div className="mt-2">{details}</div></details> : null}
      </OperatorSurface>
    </div>
  );
}

export function PageNotice({ children }: { children: ReactNode }) {
  return <OperatorSurface density="compact" className="border-brand-200 bg-brand-50 text-sm leading-6 text-brand-900">{children}</OperatorSurface>;
}

/** A durable work row with exactly one explicit transition into Workspace. */
export function PageListRow({
  title,
  description,
  metadata,
  state,
  workspaceAction,
}: {
  title: ReactNode;
  description?: ReactNode;
  metadata?: ReactNode;
  state?: ReactNode;
  workspaceAction: WorkspaceRowAction;
}) {
  return (
    <OperatorSurface density="compact" className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-base font-semibold text-slate-900">{title}</h2>
          {state}
        </div>
        {description ? <div className="mt-1 text-sm leading-6 text-slate-700">{description}</div> : null}
        {metadata ? <div className="mt-2 font-data text-xs text-slate-500">{metadata}</div> : null}
      </div>
      <Link className="shrink-0 text-sm font-medium text-brand-700 hover:text-brand-800 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2" to={workspaceAction.href} state={workspaceAction.state}>
        {workspaceAction.label}
      </Link>
    </OperatorSurface>
  );
}
